<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import type { UserAPIKeyDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { useApi } from "@/services/api";
import { useClipboard } from "@/services/clipboard";
import { formatDatetime } from "@/services/utils";
import { useAuthStore } from "@/stores/auth";
import { useModalsStore } from "@/stores/modals";
import ApiKeyCopyModalBody from "@/views/account/apikeys/ApiKeyCopyModalBody.vue";
import ApiKeyUsageTipsModalBody from "@/views/account/apikeys/ApiKeyUsageTipsModalBody.vue";
import CreateApiKeyModalBody from "@/views/account/apikeys/CreateApiKeyModalBody.vue";
import CreateApiKeyModalFooter from "@/views/account/apikeys/CreateApiKeyModalFooter.vue";

const api = useApi();
const auth = useAuthStore();
const clipboard = useClipboard();
const modals = useModalsStore();

const accountHref = computed(() => import.meta.env.VITE_VUE_IDP_PROFILE_SETTINGS_URL);
const apiKeys = ref<UserAPIKeyDto[]>([]);

const breadcrumbItems = computed(() => [
  {
    label: "Account",
  },
]);

const isLoading = ref(true);

async function openApiKeyUsageTipsModal() {
  await modals.openComponentModal({
    title: "How to use API keys",
    titleIcon: "icon-info-circle-2",
    body: ApiKeyUsageTipsModalBody,
  });
}

async function openCreateApiKeyModal() {
  const name = ref("");
  const [nameConfirmed] = await modals.openComponentModal<string>({
    title: "Create API key",
    titleIcon: "icon-key",
    body: CreateApiKeyModalBody,
    footer: CreateApiKeyModalFooter,
    models: { name: name },
  });
  if (nameConfirmed && name.value !== "" && auth.currentUser?.id) {
    const apiKey = await api.createUserApiKey(auth.currentUser.id, name.value);
    if (apiKey) {
      const hasCopied = ref(false);
      await modals.openComponentModal<string>({
        title: "API key created",
        titleIcon: "icon-key",
        body: ApiKeyCopyModalBody,
        bodyProps: { apiKey },
        models: { hasCopied },
      });
      if (!hasCopied.value) {
        clipboard.copyText(apiKey, "API key copied to clipboard");
      }
      isLoading.value = true;
      apiKeys.value = await api.fetchUserApiKeys(auth.currentUser.id);
      isLoading.value = false;
    }
  }
}

async function openDeleteApiKeyModal(apiKey: UserAPIKeyDto) {
  const confirmed = await modals.confirm({
    title: "Delete API key",
    titleIcon: "icon-key",
    message: `Do you really want to delete the API key "${apiKey.name}" ?`,
  });
  if (confirmed && auth.currentUser?.id) {
    await api.deleteUserApiKey(auth.currentUser.id, apiKey.id);
    isLoading.value = true;
    apiKeys.value = await api.fetchUserApiKeys(auth.currentUser.id);
    isLoading.value = false;
  }
}

onMounted(async () => {
  isLoading.value = true;
  apiKeys.value = await api.fetchUserApiKeys(auth.currentUser?.id ?? "");
  isLoading.value = false;
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="breadcrumbItems">
    <div v-if="isLoading" class="loading">
      <SkoreSpinner />
    </div>

    <div v-else class="account-view">
      <header>
        <div class="text-2xl-medium">My account</div>
        <div class="subtitle text-base-regular">Manage your account info.</div>
      </header>

      <section>
        <header>
          <div class="title">
            <div class="text-lg-medium">General information</div>
            <div class="subtitle text-base-regular">
              Change all items related to your personal information
            </div>
          </div>
        </header>

        <div class="account">
          <a class="skore-button ghost" :href="accountHref" target="_blank">
            <span class="label">Edit my information</span>
            <i class="icon right icon-ascending-arrow" />
          </a>
        </div>
      </section>

      <section>
        <header>
          <div class="title">
            <div class="text-lg-medium">API keys</div>
            <div class="subtitle text-base-regular">Create and manage API keys.</div>
          </div>

          <div class="actions">
            <SimpleButton
              label="Usage tips"
              icon-left="icon-info-circle-2"
              kind="ghost"
              @click="openApiKeyUsageTipsModal"
            />
            <SimpleButton
              label="Create API key"
              icon-left="icon-plus-circle"
              kind="primary"
              @click="openCreateApiKeyModal"
            />
          </div>
        </header>

        <table class="tabular-list">
          <thead>
            <tr>
              <th>Name</th>
              <th>Created at</th>
              <th>Updated at</th>
              <th>Revoke</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="apiKeys.length === 0" class="empty">
              <td colspan="4">
                <div class="text-lg-medium">No API keys found.</div>
                <div class="text-base-regular subtitle">Create a key first.</div>
              </td>
            </tr>

            <template v-else>
              <tr v-for="apiKey in apiKeys" :key="apiKey.id">
                <td>{{ apiKey.name }}</td>
                <td>{{ formatDatetime(apiKey.created_at) }}</td>
                <td>{{ formatDatetime(apiKey.updated_at) }}</td>
                <td>
                  <SimpleButton
                    label="Revoke"
                    icon-left="icon-trash"
                    kind="ghost"
                    class="revoke"
                    @click="openDeleteApiKeyModal(apiKey)"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </section>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.loading {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.account-view {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  padding: var(--numbers-32) 0;
  gap: var(--numbers-32);

  .subtitle {
    color: var(--content-tertiary);
  }

  section {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-24);

    header {
      display: flex;
      width: 100%;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: var(--numbers-8);
      }
    }

    .empty {
      text-align: center;

      td {
        padding: var(--numbers-32);
      }
    }

    .revoke {
      background-color: var(--content-negative);
      color: var(--content-primary-inverse);
    }
  }
}
</style>
